import clsx from 'clsx';
import { type ElementType, forwardRef, type ReactElement } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { colorUtils } from '../../styles/colorUtils.sty.js';
import { getSpacingSprinkles } from '../../styles/getSpacingSprinkles.js';
import { textStyle } from '../../styles/textStyle.sty.js';
import { useLayoutStyles } from '../hooks/useLayoutStyles.js';
import type { SurfaceProps } from './surface-types.js';
import * as styles from './Surface.sty.js';

const COMPONENT_NAME = 'Surface';

/**
 * Use the `Surface` component to structure and visually separate content on a page, or to create floating overlays such as menus, selects, and modals.
 * @public
 */
export const Surface: <E extends ElementType = 'div'>(
  props: SurfaceProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: SurfaceProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      elevation = 'flat',
      dragged = false,
      selected = false,
      color = 'neutral',
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      as,
      ...remainingProps
    }: SurfaceProps<E> = props;

    const ariaLabelingProps = useAriaLabelingProps(remainingProps);

    const { layoutSizeStyles, remainingProps: surfaceProps } = useLayoutStyles(
      remainingProps,
      { paddingDefaults: { padding: 24 } },
    );

    const [behavioralTrackingProps, nonBehavioralSurfaceProps] =
      useBehavioralTrackingProps(surfaceProps as Record<string, unknown>);

    const surfaceMergedProps = {
      ...mergeProps(
        // Need to cast this one as the inference of mergeProps would generate
        // a not allowed overlap. The remainingProps type is too complex due to the
        // polymorphic inheritance.
        nonBehavioralSurfaceProps as Record<string, unknown>,
        ariaLabelingProps,
      ),
    };

    const Component = as ?? 'div';

    return (
      <Component
        {...surfaceMergedProps}
        {...behavioralTrackingProps}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        className={clsx(
          'strato-surface',
          colorUtils({ color, variant: selected ? 'accent' : 'default' }),
          styles.surface({ selected }),
          focusRingStatic({ color, when: 'focus-visible' }),
          consumerClassName,
          getSpacingSprinkles(remainingProps, { padding: 24 }),
          textStyle({ textStyle: 'base' }),
        )}
        style={{ ...layoutSizeStyles, ...consumerStyle }}
        data-dt-component={dataDtComponent}
      >
        <div
          className={clsx(
            'surface-background',
            styles.pseudoElement({ elevation, dragged }),
          )}
          aria-hidden={true}
        />
        {children}
      </Component>
    );
  },
);
